import SecondaryNavigation from "@/components/navigation/SecondaryNavigation";
import { secondaryNavigationData } from "@/components/navigation/navigationData";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_BILLING_PORTAL_SESSION_MUTATION,
  CREATE_CHECKOUT_SESSION_MUTATION,
  CURRENT_SUBSCRIPTION_QUERY,
} from "@/graphql/billing";
import UsageMeter from "@/components/billing/UsageMeter";
import PlanCard from "@/components/billing/PlanCard";
import { getPlans } from "@/data/billing/plans";
import { filterNavItemsByRole } from "@/components/utils/adminUtils";

// Helper to set current section and filter by user role
const getNavWithCurrent = (currentName: string, userRole?: string) => {
  // First filter by role, then mark current section
  const filteredNav = filterNavItemsByRole(secondaryNavigationData, userRole);
  return filteredNav.map((item) => ({
    ...item,
    current: item.name === currentName,
  }));
};

export default function BillingSettings() {
  const { data, loading, error, refetch } = useQuery(
    CURRENT_SUBSCRIPTION_QUERY
  );

  const [createCheckout, { loading: creatingCheckout, error: checkoutErr }] =
    useMutation(CREATE_CHECKOUT_SESSION_MUTATION);
  const [createPortal, { loading: creatingPortal, error: portalErr }] =
    useMutation(CREATE_BILLING_PORTAL_SESSION_MUTATION);

  const defaultPlanCode =
    (import.meta as any).env.VITE_DEFAULT_PLAN_CODE || "STUDIO";

  const handleCheckout = async (planCode: string) => {
    const origin = window.location.origin;
    const res = await createCheckout({
      variables: {
        input: {
          planCode,
          successUrl: `${origin}/settings/billing?checkout=success`,
          cancelUrl: `${origin}/settings/billing?checkout=cancel`,
        },
      },
    });
    const url = res.data?.createCheckoutSession?.url;
    if (url) window.location.href = url;
  };

  const handlePortal = async () => {
    const res = await createPortal({
      variables: { input: { returnUrl: window.location.href } },
    });
    const url = res.data?.createBillingPortalSession?.url;
    if (url) window.location.href = url;
  };

  const sub = data?.currentSubscription;

  return (
    <main>
      <div className="mx-auto max-w-7xl lg:flex lg:gap-x-16 lg:px-8">
        <h1 className="sr-only">Billing Settings</h1>
        <SecondaryNavigation items={getNavWithCurrent("Billing")} />
        <main className="px-4 py-16 sm:px-6 lg:flex-auto lg:px-0 lg:py-10">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-3xl">
            <h2 className="text-xl font-semibold">Billing</h2>
            <p className="mt-2 text-gray-500">
              Manage your subscription and usage.
            </p>

            {loading && (
              <p className="mt-6 text-gray-500">Loading subscription…</p>
            )}
            {error && (
              <p className="mt-6 text-sm text-red-600">
                Failed to load subscription. Please retry.
              </p>
            )}

            {!loading && !sub && !error && (
              <div className="mt-8">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-medium">
                    No active subscription
                  </h3>
                  <p className="mt-2 text-gray-600">
                    Choose a plan to start your 30-day trial. Default
                    recommendation: {defaultPlanCode}.
                  </p>
                </div>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {getPlans().map((p) => (
                    <PlanCard
                      key={p.code}
                      plan={p}
                      disabled={creatingCheckout}
                      onSelect={(code) => handleCheckout(code)}
                    />
                  ))}
                </div>
                {checkoutErr && (
                  <div className="mt-3 text-sm text-red-600">
                    {(checkoutErr as any)?.message ||
                      "Could not create checkout session"}
                  </div>
                )}
              </div>
            )}

            {sub && (
              <div className="mt-8 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <div className="text-sm uppercase tracking-wider text-gray-500">
                      Plan
                    </div>
                    <div className="text-lg font-medium">
                      {sub.planCode || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wider text-gray-500">
                      Status
                    </div>
                    <div className="text-lg font-medium">{sub.status}</div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wider text-gray-500">
                      Renews
                    </div>
                    <div className="text-lg font-medium">
                      {sub.currentPeriodEnd
                        ? new Date(sub.currentPeriodEnd).toLocaleDateString()
                        : "—"}
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <UsageMeter
                    label="AI Generations"
                    used={sub.usage?.aiGenerationsUsed}
                    max={sub.limits?.aiGenerationsPerMonth}
                  />
                  <UsageMeter
                    label="Outfits"
                    used={sub.usage?.outfitsUsed}
                    max={sub.limits?.outfitsMax}
                  />
                  <UsageMeter
                    label="Closet Items"
                    used={sub.usage?.closetItemsUsed}
                    max={sub.limits?.closetItemsMax}
                  />
                </div>

                <div className="mt-6 flex items-center gap-3">
                  <button
                    onClick={async () => {
                      await refetch();
                      await handlePortal();
                    }}
                    disabled={creatingPortal}
                    className="inline-flex items-center rounded-full border border-gray-300 dark:border-gray-600 px-6 py-2 text-sm tracking-wider uppercase hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-60"
                  >
                    {creatingPortal ? "Opening…" : "Manage Billing"}
                  </button>
                  {portalErr && (
                    <span className="text-sm text-red-600">
                      {(portalErr as any)?.message ||
                        "Could not open billing portal"}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </main>
  );
}
